/**
 * Last-touch Notion `service_environment` cookie for GPP (WEB-2021 / WEB-1106).
 * Shop overwrites `da_service_environment=CANNABIS` on `.dransay.com`.
 * Only shop hosts write — vendor/doctor share this deployment but must not.
 */

import { getParentDomain } from "../cookie-domain";

export const SERVICE_ENVIRONMENT_COOKIE = "da_service_environment" as const;
export const CANNABIS_SERVICE_ENVIRONMENT = "CANNABIS" as const;

const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

type CookieJar = { cookie: string };

/**
 * True for shop storefront hosts (not vendor/doctor on the same app).
 * Includes staging/PR preview hosts (`app.*.dransay.me`, `app-pr*.dransay.me`).
 */
export function isShopHost(hostname: string): boolean {
	const normalized = hostname.toLowerCase().replace(/:\d+$/, "");
	if (
		normalized === "vendor.dransay.com" ||
		normalized === "doctor.dransay.com" ||
		normalized.startsWith("vendor.") ||
		normalized.startsWith("doctor.")
	) {
		return false;
	}
	if (
		normalized === "shop.dransay.com" ||
		normalized === "shop.dransay.me" ||
		(normalized.startsWith("shop.") && normalized.endsWith(".dransay.me"))
	) {
		return true;
	}
	// Platform storefront staging / PR previews use app* hosts, not shop.*
	return (
		normalized.endsWith(".dransay.me") &&
		(normalized.startsWith("app.") || /^app-pr\d+\./.test(normalized))
	);
}

/**
 * Parent cookie domain — reuses session `getParentDomain`.
 */
export function resolveShopServiceEnvironmentCookieDomain(
	hostname: string,
): string | undefined {
	return getParentDomain(hostname);
}

/**
 * Builds Set-Cookie attributes for the shared last-touch analytics cookie.
 */
export function buildCannabisServiceEnvironmentCookie(
	hostname: string,
	options: { secure: boolean } = { secure: true },
): string | null {
	if (!isShopHost(hostname)) {
		return null;
	}
	const parts = [
		`${SERVICE_ENVIRONMENT_COOKIE}=${encodeURIComponent(CANNABIS_SERVICE_ENVIRONMENT)}`,
		"Path=/",
		`Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
		"SameSite=Lax",
	];
	if (options.secure) {
		parts.push("Secure");
	}
	const domain = resolveShopServiceEnvironmentCookieDomain(hostname);
	if (domain) {
		parts.push(`Domain=${domain}`);
	}
	return parts.join("; ");
}

/**
 * GTM-gated shop writer — no-ops when tracking is off or host is not shop.
 */
export function syncCannabisServiceEnvironmentCookie(
	gtmEnabled: boolean,
	hostname: string = typeof location !== "undefined" ? location.hostname : "",
	documentCookie: CookieJar = document,
	options: { secure: boolean } = {
		secure:
			typeof location !== "undefined" ? location.protocol === "https:" : true,
	},
): void {
	if (!gtmEnabled || !hostname) {
		return;
	}
	const cookie = buildCannabisServiceEnvironmentCookie(hostname, options);
	if (!cookie) {
		return;
	}
	documentCookie.cookie = cookie;
}
